import React, { useState } from 'react';
import {
	Alert,
	Container,
	ContentLayout,
	Flashbar,
	FlashbarProps,
	Header,
	SpaceBetween,
	Wizard,
} from '@amzn/awsui-components-console';
import { useFormContext } from 'react-hook-form';
import SNOWForm from '../SNOWForm/SNOWForm';
import { ReviewSection } from './ReviewStep';
import {
	WizardStep,
	ReviewSectionData,
	ReviewableValue,
} from './@types/GenericForm.types';
import { GenericComponentData } from 'src/@types/common';
import { generateTestId } from 'src/utils/testingUtils/testingHelper';

export type { WizardStep, ReviewSectionData } from './@types/GenericForm.types';
export type { GenericComponentData } from 'src/@types/common';

export interface GenericWizardProps<T extends GenericComponentData> {
	title: string;
	description?: string;
	steps: WizardStep<T>[];
	onCancel: () => void;
	onSubmit: (data: T) => void;
	isLoading?: boolean;
	errorMessage?: string | null;
	flashBarItems?: FlashbarProps.MessageDefinition[];
	additionalContent?: React.ReactNode;
	reviewTitle?: string;
	submitButtonText?: string;
	testIdPrefix?: string;
	generateReviewSections?: (
		steps: WizardStep<T>[],
		formData: T
	) => ReviewSectionData[];
}

export default function GenericWizard<T extends GenericComponentData>({
	title,
	description,
	steps,
	onCancel,
	onSubmit,
	isLoading = false,
	errorMessage,
	flashBarItems,
	additionalContent,
	reviewTitle = 'Review Details',
	submitButtonText = 'Submit',
	testIdPrefix,
	generateReviewSections,
}: GenericWizardProps<T>) {
	const { trigger, watch } = useFormContext<T>();
	const [activeStepIndex, setActiveStepIndex] = useState(0);
	const [isLoadingNextStep, setIsLoadingNextStep] = useState(false);

	const formData = watch();
	const baseTestIdPrefix = testIdPrefix || 'generic-wizard';

	const handleFormSubmit = async () => {
		const isValid = await trigger();

		if (isValid) {
			onSubmit(formData);
		}
	};

	const visibleSteps = steps.filter(
		(step) => !step.showCondition || step.showCondition(formData)
	);

	const wizardSteps = [
		...visibleSteps.map((step) => ({
			title: step.title,
			description,
			content: (
				<Container>
					<SpaceBetween size="l">
						{step.showCondition &&
							!step.showCondition(formData) &&
							step.infoMessage && <Alert type="info">{step.infoMessage}</Alert>}

						{(!step.showCondition || step.showCondition(formData)) && (
							<SNOWForm formAttributes={step.attributes} />
						)}
					</SpaceBetween>
				</Container>
			),
		})),

		{
			title: 'Review',
			description: reviewTitle,
			content: (
				<Container>
					<SpaceBetween size="l">
						{generateReviewSections ? (
							generateReviewSections(steps, formData).map((section, index) => (
								<ReviewSection
									key={index}
									title={section.title}
									items={section.items}
								/>
							))
						) : (
							<DefaultReviewSections steps={steps} formData={formData} />
						)}
					</SpaceBetween>
				</Container>
			),
		},
	];

	return (
		<ContentLayout
			header={
				<Header variant="h1" description={description}>
					{title}
				</Header>
			}
			data-testid={generateTestId(baseTestIdPrefix, 'layout')}
		>
			<SpaceBetween size="l">
				{flashBarItems && flashBarItems.length > 0 && (
					<Flashbar
						items={flashBarItems}
						data-testid={generateTestId(baseTestIdPrefix, 'flashbar')}
					/>
				)}

				{errorMessage && (
					<Alert
						type="error"
						header="Error"
						data-testid={generateTestId(baseTestIdPrefix, 'error-alert')}
					>
						{errorMessage}
					</Alert>
				)}

				{additionalContent}

				<Wizard
					data-testid={generateTestId(baseTestIdPrefix, 'wizard')}
					i18nStrings={{
						stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
						collapsedStepsLabel: (stepNumber, stepsCount) =>
							`Step ${stepNumber} of ${stepsCount}`,
						navigationAriaLabel: 'Steps',
						cancelButton: 'Cancel',
						previousButton: 'Previous',
						nextButton: 'Next',
						submitButton: submitButtonText,
						optional: 'optional',
					}}
					onCancel={onCancel}
					onSubmit={handleFormSubmit}
					isLoadingNextStep={isLoadingNextStep || isLoading}
					onNavigate={async ({ detail: { requestedStepIndex, reason } }) => {
						if (
							reason === 'next' ||
							(reason === 'step' && activeStepIndex < requestedStepIndex)
						) {
							setIsLoadingNextStep(true);
							const valid = await trigger();

							if (valid) {
								setActiveStepIndex(requestedStepIndex);
							}

							setIsLoadingNextStep(false);
						} else {
							setActiveStepIndex(requestedStepIndex);
						}
					}}
					activeStepIndex={activeStepIndex}
					steps={wizardSteps}
				/>
			</SpaceBetween>
		</ContentLayout>
	);
}

function DefaultReviewSections<T extends GenericComponentData>({
	steps,
	formData,
}: {
	steps: WizardStep<T>[];
	formData: T;
}) {
	const reviewSections = generateDefaultReviewSections(steps, formData);

	return (
		<>
			{reviewSections.map((section, index) => (
				<ReviewSection
					key={index}
					title={section.title}
					items={section.items}
				/>
			))}
		</>
	);
}

function generateDefaultReviewSections<T extends GenericComponentData>(
	steps: WizardStep<T>[],
	formData: T
): ReviewSectionData[] {
	return steps
		.filter((step) => !step.showCondition || step.showCondition(formData))
		.map((step) => ({
			title: step.reviewGroup || step.title,
			items: step.attributes.map((attr) => ({
				label: attr.attributeName || attr.attributeId || '',
				value: getNestedValue(formData, attr.attributeId || ''),
			})),
		}));
}

function getNestedValue<T extends GenericComponentData>(
	obj: T,
	path: string
): ReviewableValue {
	try {
		return path.split('.').reduce((current: unknown, key: string) => {
			if (current && typeof current === 'object' && current !== null) {
				return (current as GenericComponentData)[key];
			}

			return undefined;
		}, obj) as ReviewableValue;
	} catch {
		return undefined;
	}
}
